import { IconDomains, IconPlus } from '@myplayer/ui';
import { useEffect, useState, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import { formatWorkspaceName } from './auth-session.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  ConfirmDialog,
  LinearSpinner,
  Select,
  Spinner,
  Tag,
  ToastNotification,
} from './components/ui/primitives.js';
import {
  AddCustomDomainModal,
  DefaultDomainCard,
  DomainTileCard,
  EditCustomDomainModal,
  filterOutPlatformDomains,
  formatDomainStatusLabel,
  getDomainFailureReason,
  mapSnapshotToDomains,
  validateCustomHostnameInput,
  type CustomDomainItem,
} from './domains-modals.js';
import * as fb from './firebase.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { resolveEffectiveHostname, resolveEffectiveOrigin } from './use-workspace-identifiers.js';

export interface DomainsPageProps {
  readonly initialDomains?: readonly CustomDomainItem[];
  readonly embedded?: boolean;
  readonly onRequestDomain?: typeof fb.callRequestCustomDomainFunction;
  readonly onVerifyDomain?: typeof fb.callVerifyCustomDomainFunction;
  readonly onRecheckDomain?: typeof fb.callRecheckCustomDomainFunction;
  readonly onOffboardDomain?: typeof fb.callOffboardCustomDomainFunction;
}

export function DomainsPage({
  initialDomains,
  embedded = false,
  onRequestDomain,
  onVerifyDomain,
  onRecheckDomain,
  onOffboardDomain,
}: DomainsPageProps = {}): ReactElement {
  const { session, can, availableWorkspaces } = useAuth();
  const orgId = session?.orgId ?? '';
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(session?.activeWorkspaceId ?? '');
  const workspaceId = selectedWorkspaceId || session?.activeWorkspaceId || '';
  const workspaceName =
    availableWorkspaces.find((ws) => ws.workspaceId === workspaceId)?.name ??
    formatWorkspaceName(undefined, workspaceId);
  const canManageDomains = can('domain:manage');
  const canVerifyDomains = can('domain:verify');
  const effectiveHostname = resolveEffectiveHostname();
  const effectiveOrigin = resolveEffectiveOrigin();

  const [domains, setDomains] = useState<readonly CustomDomainItem[]>(() =>
    filterOutPlatformDomains(initialDomains ?? [], effectiveHostname),
  );
  const [loading, setLoading] = useState<boolean>(
    initialDomains === undefined && Boolean(orgId) && Boolean(workspaceId),
  );
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<CustomDomainItem | null>(null);
  const [removingDomain, setRemovingDomain] = useState<CustomDomainItem | null>(null);
  const [hostnameInput, setHostnameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  useEffect(() => {
    if (initialDomains !== undefined) {
      setDomains(filterOutPlatformDomains(initialDomains, effectiveHostname));
      setLoading(false);
      return;
    }
    if (!orgId || !workspaceId) {
      setLoading(false);
      return;
    }
    let unsubscribe: (() => void) | undefined;
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const { collection, onSnapshot } = await import('firebase/firestore');
        if (!active) return;
        const { db } = fb.getFirebaseClients();
        const colRef = collection(db, 'organisations', orgId, 'workspaces', workspaceId, 'domains');
        unsubscribe = onSnapshot(
          colRef,
          (snap) => {
            setDomains(mapSnapshotToDomains(snap.docs, orgId, workspaceId, effectiveHostname));
            setLoading(false);
          },
          (err) => {
            setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.domainsVerifyFallback));
            setLoading(false);
          },
        );
      } catch {
        setLoading(false);
      }
    })();
    return () => {
      active = false;
      if (unsubscribe) unsubscribe();
    };
  }, [orgId, workspaceId, initialDomains, effectiveHostname]);

  useEffect(() => {
    if (!canManageDomains || typeof window === 'undefined') return;
    const handleOpenAddDomain = (): void => {
      setHostnameInput('');
      setErrorMessage(null);
      setAddModalOpen(true);
    };
    window.addEventListener('mp:open-add-domain', handleOpenAddDomain);
    return () => window.removeEventListener('mp:open-add-domain', handleOpenAddDomain);
  }, [canManageDomains]);

  const requestFn = onRequestDomain ?? onVerifyDomain ?? fb.callRequestCustomDomainFunction;
  const verifyFn = onVerifyDomain ?? fb.callVerifyCustomDomainFunction;
  const recheckFn = onRecheckDomain ?? onVerifyDomain ?? fb.callRecheckCustomDomainFunction;
  const offboardFn = onOffboardDomain ?? fb.callOffboardCustomDomainFunction;

  const handleDomainCallableMutation = async (
    mode: 'request' | 'verify' | 'recheck',
    rawHostname: string,
    isPrimaryOverride?: boolean,
  ): Promise<void> => {
    const validated = validateCustomHostnameInput(rawHostname, effectiveHostname);
    if (!validated.ok) {
      setErrorMessage(validated.error);
      return;
    }
    setErrorMessage(null);
    setFeedbackMessage(null);
    setSubmitting(true);
    try {
      const fn = mode === 'request' ? requestFn : mode === 'recheck' ? recheckFn : verifyFn;
      const res = await fn({ orgId, workspaceId, hostname: validated.hostname });
      const prevDoc = domains.find((d) => d.hostname === res.hostname);
      const nextPrimary = isPrimaryOverride ?? prevDoc?.isPrimary ?? false;
      const nowIso = res.verifiedAt ?? new Date().toISOString();
      const failMsg =
        res.failureReason ??
        (res.status === 'FAILED' || res.status === 'FAILING' ? prevDoc?.failureReason : undefined);
      const acme = res.acmeChallengeRecord ?? prevDoc?.acmeChallengeRecord;
      const nextDoc: CustomDomainItem = {
        ...prevDoc,
        hostname: res.hostname,
        orgId,
        workspaceId,
        status: res.status,
        ...(res.plainStatus !== undefined ? { plainStatus: res.plainStatus } : {}),
        verificationToken:
          res.txtValue ?? res.verificationToken ?? prevDoc?.verificationToken ?? '',
        ...(res.txtHost !== undefined ? { txtHost: res.txtHost } : {}),
        tlsStatus: res.tlsStatus,
        isPrimary: nextPrimary,
        verifiedAt: res.verifiedAt,
        ...(res.cnameTarget !== undefined ? { cnameTarget: res.cnameTarget } : {}),
        ...(acme !== undefined ? { acmeChallengeRecord: acme } : {}),
        ...(failMsg !== undefined ? { failureReason: failMsg } : {}),
        createdAt: prevDoc?.createdAt ?? nowIso,
        updatedAt: nowIso,
        updatedBy: session?.uid ?? '',
      };
      setDomains((prev) => {
        const list = prev.some((d) => d.hostname === res.hostname)
          ? prev.map((d) => (d.hostname === res.hostname ? nextDoc : d))
          : [...prev, nextDoc];
        return filterOutPlatformDomains(
          nextPrimary
            ? list.map((d) => (d.hostname === nextDoc.hostname ? d : { ...d, isPrimary: false }))
            : list,
          effectiveHostname,
        );
      });
      setHostnameInput('');
      setAddModalOpen(false);
      setEditingDomain(null);
      const label = formatDomainStatusLabel(res.status, res.tlsStatus);
      const reason = getDomainFailureReason(nextDoc);
      setFeedbackMessage(
        `Domain ${res.hostname} status: ${res.status} (${reason !== null ? `${label}: ${reason}` : label})`,
      );
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.domainsVerifyFallback));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmRemoveDomain = async (): Promise<void> => {
    if (!removingDomain || !canManageDomains) return;
    const targetHostname = removingDomain.hostname;
    setErrorMessage(null);
    setFeedbackMessage(null);
    setSubmitting(true);
    try {
      await offboardFn({ orgId, workspaceId, hostname: targetHostname });
      setDomains((prev) => prev.filter((d) => d.hostname !== targetHostname));
      setRemovingDomain(null);
      setEditingDomain((prev) => (prev?.hostname === targetHostname ? null : prev));
      setFeedbackMessage(`Domain ${targetHostname} removed.`);
    } catch (err: unknown) {
      setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.domainsVerifyFallback));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {!embedded && <h1 className="text-page-title">Resolver Domains</h1>}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-12">
        <DefaultDomainCard effectiveOrigin={effectiveOrigin} />
        <Card className="xl:col-span-8" data-testid="custom-domains-card">
          {submitting && <LinearSpinner />}
          <CardHeader>
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <CardIcon>
                <IconDomains />
              </CardIcon>
              <CardTitle>Custom Brand Domains</CardTitle>
              <Tag variant="outline" size="sm">
                {domains.length}
              </Tag>
            </div>
            {canManageDomains ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={submitting}
                className="gap-1.5"
                data-testid="open-add-domain-btn"
                aria-label="Add & Verify Domain"
                onClick={() => {
                  setHostnameInput('');
                  setErrorMessage(null);
                  setAddModalOpen(true);
                }}
              >
                <IconPlus />
                <span>Add Domain</span>
              </Button>
            ) : (
              <span
                role="status"
                data-testid="domains-readonly-notice"
                className="text-[11px] font-medium text-fg-muted"
              >
                {formatCapabilityRestrictionNotice({
                  capability: 'domain:manage',
                  actionSummary: 'register or verify custom resolver domains',
                  callerRole: session?.personaRole,
                })}
              </span>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardDescription>
                Use your own brand domain on printed QR codes and check connection status for{' '}
                <span className="font-medium text-fg">{workspaceName}</span>
              </CardDescription>
              {availableWorkspaces.length > 1 && (
                <Select
                  aria-label="Filter domains by workspace"
                  data-testid="domains-workspace-filter"
                  value={workspaceId}
                  onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                >
                  {availableWorkspaces.map((ws) => (
                    <option key={ws.workspaceId} value={ws.workspaceId}>
                      {ws.name}
                    </option>
                  ))}
                </Select>
              )}
            </div>
            <ToastNotification
              message={feedbackMessage}
              onDismiss={() => setFeedbackMessage(null)}
            />
            {errorMessage !== null && !addModalOpen && editingDomain === null && (
              <p
                role="alert"
                className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
              >
                {errorMessage}
              </p>
            )}
            <div className="space-y-3 border-t border-subtle pt-3">
              {loading ? (
                <div
                  role="status"
                  data-testid="domains-loading-state"
                  className="flex items-center justify-center gap-2.5 py-8 text-[13px] text-fg-secondary"
                >
                  <Spinner size={18} />
                  <span>{ADMIN_STRINGS.loading.domains}</span>
                </div>
              ) : domains.length === 0 ? (
                <p
                  data-testid="domains-empty-state"
                  className="py-6 text-center text-[13px] text-fg-secondary"
                >
                  {ADMIN_STRINGS.empty.domains}
                </p>
              ) : (
                domains.map((domain) => (
                  <DomainTileCard
                    key={domain.hostname}
                    domain={domain}
                    effectiveHostname={effectiveHostname}
                    canVerifyDomains={canVerifyDomains}
                    canManageDomains={canManageDomains}
                    submitting={submitting}
                    onVerify={(d) =>
                      void handleDomainCallableMutation('verify', d.hostname, d.isPrimary)
                    }
                    onRecheck={(d) =>
                      void handleDomainCallableMutation('recheck', d.hostname, d.isPrimary)
                    }
                    onEdit={(d) => {
                      setErrorMessage(null);
                      setEditingDomain(d);
                    }}
                    onRequestRemove={(d) => {
                      setErrorMessage(null);
                      setRemovingDomain(d);
                    }}
                  />
                ))
              )}
            </div>
          </CardContent>
          <AddCustomDomainModal
            open={addModalOpen}
            orgId={orgId}
            workspaceId={workspaceId}
            effectiveHostname={effectiveHostname}
            hostnameInput={hostnameInput}
            setHostnameInput={setHostnameInput}
            submitting={submitting}
            errorMessage={errorMessage}
            onClose={() => {
              setAddModalOpen(false);
              setErrorMessage(null);
            }}
            onSubmit={(h, p) => void handleDomainCallableMutation('request', h, p)}
          />
          <EditCustomDomainModal
            domain={editingDomain}
            effectiveHostname={effectiveHostname}
            submitting={submitting}
            errorMessage={errorMessage}
            onClose={() => {
              setEditingDomain(null);
              setErrorMessage(null);
            }}
            onSave={(d, p) => void handleDomainCallableMutation('verify', d.hostname, p)}
          />
          <ConfirmDialog
            open={removingDomain !== null}
            title={removingDomain ? `Remove ${removingDomain.hostname}?` : 'Remove domain?'}
            description={
              removingDomain
                ? `This will disconnect ${removingDomain.hostname} from ${workspaceId}. Printed QR codes using this domain will stop resolving until it is added and verified again.`
                : 'This will disconnect the custom domain from this workspace.'
            }
            confirmLabel="Remove domain"
            variant="danger"
            submitting={submitting}
            data-testid="confirm-remove-domain-modal"
            confirmTestId="confirm-remove-domain-btn"
            onCancel={() => setRemovingDomain(null)}
            onConfirm={() => void handleConfirmRemoveDomain()}
          />
        </Card>
      </div>
    </div>
  );
}
